/**
 * Servicio para tipos de cambio USD → múltiples monedas (ExchangeRate.host)
 * PEN, COP, ARS, CLP desde API. VEF (bolívares) siempre manual.
 */

const API_BASE = "https://api.exchangerate.host";
const CACHE_KEY = "controlcash_exchange_rates";
const CACHE_HOURS = 24;
const SUPPORTED_CURRENCIES = "PEN,COP,ARS,CLP";

const DEFAULT_RATES = { PEN: 3.75, USD: 1, COP: 4100, ARS: 1000, CLP: 950, VEF: 36 };

const getCachedRates = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { rates, fetchedAt } = JSON.parse(cached);
    const hoursSince = (Date.now() - fetchedAt) / (1000 * 60 * 60);
    if (hoursSince < CACHE_HOURS && rates) return rates;
    return null;
  } catch {
    return null;
  }
};

const setCachedRates = (rates) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() }));
  } catch (e) {
    console.error("Error caching rates:", e);
  }
};

/**
 * Obtiene tipos de cambio 1 USD = X para PEN, COP, ARS, CLP (API).
 * VEF (bolívares) se configura siempre de forma manual.
 */
export const fetchExchangeRates = async (apiKey) => {
  if (!apiKey || !apiKey.trim()) {
    const cached = getCachedRates();
    return cached ? { rates: cached, source: "cache" } : null;
  }

  const cached = getCachedRates();
  if (cached) return { rates: cached, source: "cache" };

  try {
    const url = `${API_BASE}/live?access_key=${encodeURIComponent(apiKey)}&currencies=${SUPPORTED_CURRENCIES}&source=USD`;
    const res = await fetch(url);
    const data = await res.json();

    if (!data.success || !data.quotes) {
      console.warn("ExchangeRate API: response inválida", data);
      return null;
    }

    const rates = {
      PEN: parseFloat(data.quotes.USDPEN) || DEFAULT_RATES.PEN,
      USD: 1,
      COP: parseFloat(data.quotes.USDCOP) || DEFAULT_RATES.COP,
      ARS: parseFloat(data.quotes.USDARS) || DEFAULT_RATES.ARS,
      CLP: parseFloat(data.quotes.USDCLP) || DEFAULT_RATES.CLP,
      VEF: (cached && cached.VEF) ?? DEFAULT_RATES.VEF
    };

    if (isNaN(rates.PEN) || rates.PEN <= 0) rates.PEN = DEFAULT_RATES.PEN;
    setCachedRates(rates);
    return { rates, source: "api" };
  } catch (err) {
    console.warn("ExchangeRate API error:", err.message);
    return null;
  }
};

/** @deprecated Usar fetchExchangeRates */
export const fetchUsdToPenRate = async (apiKey) => {
  const result = await fetchExchangeRates(apiKey);
  return result?.rates?.PEN ? { rate: result.rates.PEN, source: result.source } : null;
};

export const forceFetchExchangeRates = async (apiKey) => {
  if (!apiKey?.trim()) return null;
  try {
    const url = `${API_BASE}/live?access_key=${encodeURIComponent(apiKey)}&currencies=${SUPPORTED_CURRENCIES}&source=USD`;
    const res = await fetch(url);
    const data = await res.json();
    if (!data.success || !data.quotes) return null;

    const cached = getCachedRates();
    const rates = {
      PEN: parseFloat(data.quotes.USDPEN) || DEFAULT_RATES.PEN,
      USD: 1,
      COP: parseFloat(data.quotes.USDCOP) || DEFAULT_RATES.COP,
      ARS: parseFloat(data.quotes.USDARS) || DEFAULT_RATES.ARS,
      CLP: parseFloat(data.quotes.USDCLP) || DEFAULT_RATES.CLP,
      VEF: (cached && cached.VEF) ?? DEFAULT_RATES.VEF
    };
    if (isNaN(rates.PEN) || rates.PEN <= 0) rates.PEN = DEFAULT_RATES.PEN;
    setCachedRates(rates);
    return { rates, source: "api" };
  } catch (err) {
    console.warn("ExchangeRate API error:", err.message);
    return null;
  }
};

/** @deprecated Usar forceFetchExchangeRates */
export const forceFetchUsdToPenRate = async (apiKey) => {
  const result = await forceFetchExchangeRates(apiKey);
  return result?.rates?.PEN ? { rate: result.rates.PEN, source: result.source } : null;
};

export const getCachedRateInfo = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { rates, fetchedAt } = JSON.parse(cached);
    return { rate: rates?.PEN, fetchedAt };
  } catch {
    return null;
  }
};
